import { useState, useEffect, useRef } from 'react';
import { View, Pressable, StyleSheet, Animated, LayoutChangeEvent, Platform } from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { ThemedText } from '@/components/ui/Text';
import { hapticSelection } from '@/utils/haptics';
import { traceModuleEvaluation } from '@/utils/moduleTrace';
import { TripErrorBoundary } from '@/utils/errorBoundary';
import DocumentsScreen from './documents';
import ProfileScreen from './profile';

traceModuleEvaluation('app/(tabs)/more');

type MoreTab = 'documents' | 'profile';

export default function MoreScreen() {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const router = useRouter();
  const params = useLocalSearchParams();
  const [activeTab, setActiveTab] = useState<MoreTab>((params.tab as MoreTab) || 'documents');
  
  // Animation values for modern tab indicator
  const indicatorPosition = useRef(new Animated.Value(activeTab === 'documents' ? 0 : 1)).current;
  const [tabLayouts, setTabLayouts] = useState<{
    documents: { width: number; x: number } | null;
    profile: { width: number; x: number } | null;
  }>({ documents: null, profile: null });

  // Sync activeTab with params
  useEffect(() => {
    if (params.tab && (params.tab === 'documents' || params.tab === 'profile')) {
      setActiveTab(params.tab as MoreTab);
    }
  }, [params.tab]);

  // Animate indicator when tab changes
  useEffect(() => {
    Animated.spring(indicatorPosition, {
      toValue: activeTab === 'documents' ? 0 : 1,
      useNativeDriver: false, // Need to use JS driver for width animation
      tension: 68,
      friction: 8,
    }).start();
  }, [activeTab, indicatorPosition]);

  const handleTabChange = (tab: MoreTab) => {
    if (tab !== activeTab) {
      hapticSelection();
      setActiveTab(tab);
      // Update URL params to reflect active tab without navigating away
      router.setParams({ tab });
    }
  };

  // Calculate indicator position and width
  const indicatorStyle = (() => {
    const docsLayout = tabLayouts.documents;
    const profileLayout = tabLayouts.profile;

    if (!docsLayout || !profileLayout) {
      return { left: 0, width: 0 };
    }

    const translateX = indicatorPosition.interpolate({
      inputRange: [0, 1],
      outputRange: [docsLayout.x, profileLayout.x],
    });

    const width = indicatorPosition.interpolate({
      inputRange: [0, 1],
      outputRange: [docsLayout.width, profileLayout.width],
    });

    return {
      left: translateX,
      width: width,
    };
  })();

  const handleTabLayout = (tab: MoreTab) => (event: LayoutChangeEvent) => {
    const { width, x } = event.nativeEvent.layout;
    setTabLayouts((prev) => ({
      ...prev,
      [tab]: { width, x },
    }));
  };

  return (
    <View style={{ flex: 1, backgroundColor: theme.colors.background }}>
      {/* Modern Tab Bar with Segmented Control Style */}
      <View 
        style={[
          styles.tabBarContainer,
          { 
            backgroundColor: theme.colors.surface,
            borderBottomColor: theme.colors.borderMuted,
            paddingTop: insets.top + theme.spacing(1),
            paddingBottom: theme.spacing(1),
          }
        ]}
      >
        <View 
          style={[
            styles.tabBar,
            {
              backgroundColor: theme.colorScheme === 'dark' 
                ? 'rgba(255, 255, 255, 0.08)' 
                : 'rgba(19, 42, 19, 0.04)',
            }
          ]}
        >
          {/* Animated Pill Indicator */}
          {tabLayouts.documents && tabLayouts.profile && (
            <Animated.View
              style={[
                styles.indicator,
                {
                  backgroundColor: theme.colors.primary,
                  left: indicatorStyle.left,
                  width: indicatorStyle.width,
                },
              ]}
            />
          )}

          {/* Documents Tab */}
          <Pressable
            onPress={() => handleTabChange('documents')}
            onLayout={handleTabLayout('documents')}
            style={({ pressed }) => [
              styles.tab,
              pressed && styles.tabPressed,
            ]}
            accessibilityRole="button"
            accessibilityLabel="Documents tab"
            accessibilityHint="Switch to documents view"
            accessibilityState={{ selected: activeTab === 'documents' }}
          >
            <Ionicons
              name={activeTab === 'documents' ? 'document-text' : 'document-text-outline'}
              size={activeTab === 'documents' ? 22 : 20}
              color={activeTab === 'documents' ? theme.colors.onPrimary : theme.colors.text}
              style={[styles.tabIcon, activeTab !== 'documents' && theme.colorScheme === 'dark' && styles.tabInactiveGlyph]}
            />
            <ThemedText
              variant="bodyStrong"
              color={activeTab === 'documents' ? theme.colors.onPrimary : theme.colors.text}
              style={[
                styles.tabLabel,
                activeTab === 'documents' && styles.tabLabelActive,
                activeTab !== 'documents' && theme.colorScheme === 'dark' && styles.tabInactiveLabel,
              ]}
            >
              Documents
            </ThemedText>
          </Pressable>

          {/* Profile Tab */}
          <Pressable
            onPress={() => handleTabChange('profile')}
            onLayout={handleTabLayout('profile')}
            style={({ pressed }) => [
              styles.tab,
              pressed && styles.tabPressed,
            ]}
            accessibilityRole="button"
            accessibilityLabel="Profile tab"
            accessibilityHint="Switch to profile and settings view"
            accessibilityState={{ selected: activeTab === 'profile' }}
          >
            <Ionicons
              name={activeTab === 'profile' ? 'person-circle' : 'person-circle-outline'}
              size={activeTab === 'profile' ? 22 : 20}
              color={activeTab === 'profile' ? theme.colors.onPrimary : theme.colors.text}
              style={[styles.tabIcon, activeTab !== 'profile' && theme.colorScheme === 'dark' && styles.tabInactiveGlyph]}
            />
            <ThemedText
              variant="bodyStrong"
              color={activeTab === 'profile' ? theme.colors.onPrimary : theme.colors.text}
              style={[
                styles.tabLabel,
                activeTab === 'profile' && styles.tabLabelActive,
                activeTab !== 'profile' && theme.colorScheme === 'dark' && styles.tabInactiveLabel,
              ]}
            >
              Profile
            </ThemedText>
          </Pressable>
        </View>
      </View>

      {/* Tab Content - Conditionally render based on active tab */}
      {/* Inner screens already manage their own padding; avoid adding extra bottom space here */}
      <View style={styles.content}>
        {activeTab === 'documents' ? (
          <TripErrorBoundary name="MoreDocumentsPanel">
            <DocumentsScreen hideHeader={true} />
          </TripErrorBoundary>
        ) : (
          <TripErrorBoundary name="MoreProfilePanel">
            <ProfileScreen hideHeader={true} />
          </TripErrorBoundary>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  tabBarContainer: {
    paddingHorizontal: 16,
    paddingTop: 8,
    paddingBottom: 12,
    borderBottomWidth: 1,
    zIndex: 10,
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.05,
        shadowRadius: 3,
      },
      android: {
        elevation: 2,
      },
    }),
  },
  tabBar: {
    flexDirection: 'row',
    borderRadius: 12,
    padding: 4,
    position: 'relative',
    minHeight: 48, // Better touch target
  },
  indicator: {
    position: 'absolute',
    top: 4,
    bottom: 4,
    borderRadius: 10,
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.1,
        shadowRadius: 2,
      },
      android: {
        elevation: 2,
      },
    }),
  },
  tab: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 10,
    minHeight: 44, // Accessibility: minimum touch target
    zIndex: 1,
  },
  tabPressed: {
    opacity: 0.7,
  },
  tabIcon: {
    marginRight: 8,
  },
  tabLabel: {
    fontSize: 15,
    fontWeight: '500',
    letterSpacing: 0.1,
  },
  tabLabelActive: {
    fontWeight: '600',
  },
  /** Inactive segment on dark charcoal strip: full text token + slight dim reads reliably (vs. textSecondary alone). */
  tabInactiveLabel: {
    opacity: 0.85,
  },
  tabInactiveGlyph: {
    opacity: 0.85,
  },
  content: {
    flex: 1,
  },
});

